import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import {
  useGetCartItems,
  useGetMenuItems
} from 'bento-ordering/menus/selectors/index';
import {
  selectCartIsGroupOrder,
  selectIsGroupOrderCurrentDinerAdmin,
  selectCartGroupOrderSpendingLimit,
  selectCurentDinerAmountSpent
} from 'bento-ordering/cart/selectors/index';
import { buildFormModificationsForPricePreview } from 'helpers/forms/menuItem';
import { getProductPricePreview } from 'bento-ordering/menus/actions/productPreview';
import { hasExceededSpendingLimit } from 'bento-ordering/group_ordering/utils';

export const useIsUpsellEnabled = () =>
  useSelector(state => state.menu.is_upsell_enabled);

export const useGetUpsellType = () =>
  useSelector(state => state.menu.upsell_type);

export const useGetUpsellItemIds = () =>
  useSelector(state => state.menu.upsell_items) || [];

export const useGetSpendingLimitInfo = () => {
  const isGroupOrder = useSelector(selectCartIsGroupOrder);
  const isCurrentDinerAdmin = useSelector(selectIsGroupOrderCurrentDinerAdmin);

  const perPersonSpendinglimit = useSelector(selectCartGroupOrderSpendingLimit);
  const currentDinerAmountSpent = useSelector(selectCurentDinerAmountSpent);

  return {
    isGroupOrder,
    isCurrentDinerAdmin,
    perPersonSpendinglimit,
    currentDinerAmountSpent
  };
};

export const useGetUpsellItemsWithModifiers = upsellItems => {
  const menuItemsDetail = useSelector(state => state?.menuItemsDetail);

  return upsellItems.map(upsellItem => ({
    ...upsellItem,
    product: {
      ...upsellItem.product,
      ...menuItemsDetail.byId[`${upsellItem.product.id}`]
    }
  }));
};

const useGetUpsellItemsBelowSpendingLimit = (upsellItems = []) => {
  const [validUpsellItems, setValidUpsellItems] = useState(upsellItems);
  const dispatch = useDispatch();

  const {
    isGroupOrder,
    isCurrentDinerAdmin,
    perPersonSpendinglimit,
    currentDinerAmountSpent
  } = useGetSpendingLimitInfo();

  const shouldFilterBySpendingLimit =
    isGroupOrder && !isCurrentDinerAdmin && perPersonSpendinglimit;

  useEffect(() => {
    const asyncFilter = async (unfilteredArr, predicate) => {
      const resultsBoolArr = await Promise.all(unfilteredArr.map(predicate));
      return unfilteredArr.filter((_, index) => resultsBoolArr[index]);
    };

    const filterUpsellItemsBySpendingLimit = async () => {
      const filteredUpsellItemsBySpendingLimit = await asyncFilter(
        upsellItems,
        async item => {
          const requiredFields = buildFormModificationsForPricePreview(
            item.product.modifiers,
            {}
          );

          const response = await dispatch(
            getProductPricePreview(item.product.id, requiredFields)
          );

          const pricePreview = response.payload.final_price;

          return !hasExceededSpendingLimit(
            pricePreview,
            perPersonSpendinglimit,
            currentDinerAmountSpent
          );
        }
      );

      setValidUpsellItems(filteredUpsellItemsBySpendingLimit);
    };

    if (
      upsellItems.length &&
      currentDinerAmountSpent &&
      shouldFilterBySpendingLimit
    ) {
      filterUpsellItemsBySpendingLimit();
    }
  }, [currentDinerAmountSpent]);

  return shouldFilterBySpendingLimit ? validUpsellItems : upsellItems;
};

export const useGetEligibleMenuUpsellItems = () => {
  const [cartItems, menuItems, upsellItemIds, upsellType] = [
    useGetCartItems(),
    useGetMenuItems(),
    useGetUpsellItemIds(),
    useGetUpsellType()
  ];

  const upsellItemsNotInCart = menuItems.filter(
    item =>
      item.inventory !== 0 &&
      upsellItemIds.includes(item.product.id) &&
      !cartItems.includes(item.product.id)
  );

  const upsellItemsWithModifiers = useGetUpsellItemsWithModifiers(
    upsellItemsNotInCart
  );
  const eligibleUpsellItems = useGetUpsellItemsBelowSpendingLimit(
    upsellItemsWithModifiers
  );

  return { items: eligibleUpsellItems, upsellType };
};

export const selectIsUpsellItem = (lastItem, menu) => {
  const productId = get(lastItem, 'add.product', null);
  const upsellItems = get(menu, 'upsell_items', []);

  return !!upsellItems.find(id => productId === id);
};

export const selectIsUpsellItemAddedFromCart = (lastItem, menu, router) =>
  selectIsUpsellItem(lastItem, menu) && router.location.pathname === '/cart';
